import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { getSource, useSources } from "../../sources";
import { useAdjacentEpisodes } from "../../lib/episodeNav";
import { playerExit } from "../../lib/playerExit";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { cachedPlayerPrefs, fetchPlayerPrefs } from "../../lib/playerPrefs";
import { Spinner } from "../../components/ui";
import { playerIconBtn } from "../../components/player/chromeClasses";
import { AudioTrackIcon, CaptionsIcon, ChaptersIcon, GaugeIcon, GearIcon } from "../../components/icons";
import { ChaptersMenu } from "../../components/player/ChaptersMenu";
import { SkipMarkerPill } from "../../components/player/SkipMarkerPill";
import { upNextStartS } from "../../lib/analysis";
import { usePartAnalysis } from "../../lib/usePartAnalysis";
import { PlayerMetadata } from "../../components/player/PlayerMetadata";
import { PlayerOverlays } from "../../components/player/PlayerOverlays";
import { PlayerTopBar, toggleFullscreen } from "../../components/player/PlayerTopBar";
import { SeekBar } from "../../components/player/SeekBar";
import { SettingsMenu, type SettingsPanel } from "../../components/player/SettingsMenu";
import { ShortcutsHelp } from "../../components/player/ShortcutsHelp";
import { SubtitleSearchDialog } from "../../components/subtitle-search-dialog";
import { SubtitleOverlay } from "../../components/player/SubtitleOverlay";
import { TapLayer } from "../../components/player/TapLayer";
import { ElapsedReadout, RemainingReadout } from "../../components/player/TimeReadout";
import { AudioMenu, SubtitlesMenu, audioStreams } from "../../components/player/TracksMenu";
import { TransportControls } from "../../components/player/TransportControls";
import { UpNextCard } from "../../components/player/UpNextCard";
import { useMediaQuery } from "../../components/player/useMediaQuery";
import { useMediaSession } from "../../components/player/useMediaSession";
import { usePlaybackEngine } from "../../components/player/usePlaybackEngine";
import { usePlayerChrome } from "../../components/player/usePlayerChrome";
import { stepSpeed, usePlayerKeys } from "../../components/player/usePlayerKeys";
import { usePlayerSettings } from "../../components/player/playerSettings";
import { useUpNext } from "../../components/player/useUpNext";
import { useWakeLock } from "../../components/player/useWakeLock";
import { renditionRef, type TrackRef } from "../../lib/subtitlePick";

export const Route = createFileRoute("/_authed/player/$sourceId/$itemId")({
  // t: start at this many seconds (a chapter click) instead of the resume
  // point; malformed values are dropped, never a broken player.
  validateSearch: z.object({
    restart: z.boolean().optional(),
    t: z.number().nonnegative().optional().catch(undefined),
  }),
  component: Player,
});

type MenuId = "none" | "subs" | "audio" | "chapters" | "settings";

function Player() {
  // Covered by the black stage, but keep the shell's backdrop in art mode
  // so item → player → item returns to the wash, not the still.
  useAmbientBackdrop();
  const { sourceId, itemId } = Route.useParams();
  const { restart, t } = Route.useSearch();
  const { isLoading: sourcesLoading } = useSources();
  const source = getSource(sourceId);

  if (!source) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black text-[var(--rago-text-2)]">
        {sourcesLoading ? <Spinner size={40} /> : <span>Server unavailable.</span>}
      </div>
    );
  }
  return (
    <PlayerInner
      key={itemId}
      sourceId={sourceId}
      itemId={itemId}
      restart={restart === true}
      startAtS={t}
    />
  );
}

function PlayerInner(props: { sourceId: string; itemId: string; restart: boolean; startAtS?: number }) {
  const { sourceId, itemId, restart, startAtS } = props;
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const source = getSource(sourceId)!;
  const isServer = source.kind === "server";

  const videoRef = useRef<HTMLVideoElement>(null);
  const [menu, setMenu] = useState<MenuId>("none");
  const [settingsPanel, setSettingsPanel] = useState<SettingsPanel>("root");
  const [helpOpen, setHelpOpen] = useState(false);
  const [subSearchOpen, setSubSearchOpen] = useState(false);
  // Cursor resting on the control bars keeps them visible (Plex idiom).
  const [hoverChrome, setHoverChrome] = useState(false);
  const settings = usePlayerSettings();
  const wide = useMediaQuery("(min-width: 640px)");
  const coarse = useMediaQuery("(pointer: coarse)");

  // Account-level player prefs (subtitle style, burn policy) — the same
  // query the settings pages use; the localStorage mirror answers offline.
  const prefsQuery = useQuery({
    queryKey: ["me", "settings"],
    queryFn: fetchPlayerPrefs,
    placeholderData: cachedPlayerPrefs(),
    retry: 1,
  });
  const prefs = prefsQuery.data ?? cachedPlayerPrefs();

  const item = useQuery({
    queryKey: ["item", sourceId, itemId],
    queryFn: () => source.getItem(itemId),
  });

  const { prev, next } = useAdjacentEpisodes(source, item.data);

  // Back / Escape / ended: return to the page that opened the player and
  // consume the player's history entry, so that page's own "← Back" never
  // re-enters it (pushing the item page here used to leave the player one
  // step behind). A deep link has nothing to go back to: land on the item's
  // page — a show for an episode — replacing the player entry (lib/playerExit).
  const grandparentId = item.data?.episode?.grandparentId;
  const exit = useCallback(() => {
    const target = playerExit(canGoBack, itemId, grandparentId);
    if (target.kind === "back") {
      router.history.back();
      return;
    }
    void navigate({
      to: "/item/$sourceId/$itemId",
      params: { sourceId, itemId: target.itemId },
      replace: true,
    });
  }, [canGoBack, router, navigate, sourceId, itemId, grandparentId]);

  const goToEpisode = useCallback(
    (epId: string) => {
      void navigate({
        to: "/player/$sourceId/$itemId",
        params: { sourceId, itemId: epId },
        replace: true, // browser Back exits the player, not the previous episode
      });
    },
    [navigate, sourceId],
  );

  const onEnded = useCallback(() => {
    // With a next episode, Up Next owns the ended screen.
    if (!next) exit();
  }, [next, exit]);

  const { snap, actions } = usePlaybackEngine({
    source,
    sourceId,
    itemId,
    item: item.data,
    restart,
    startAtMs: startAtS !== undefined ? Math.round(startAtS * 1000) : undefined,
    videoRef,
    onEnded,
  });

  // Chapters, skip markers and the scrubber previews of the part being
  // played (timings attach to the file: a second version differs).
  const analysis = usePartAnalysis(source, snap.playback?.partId);

  const upNext = useUpNext({
    itemId,
    next,
    videoRef,
    autoPlay: settings.autoPlayNext,
    creditsStartS: upNextStartS(analysis.markers),
    onPlayNext: (n) => goToEpisode(n.id),
  });

  const chrome = usePlayerChrome({
    pinned:
      !snap.playing ||
      hoverChrome ||
      menu !== "none" ||
      helpOpen ||
      subSearchOpen ||
      upNext.visible ||
      snap.terminated !== null,
  });

  // Stable identities for the Media Session hook: inline closures here
  // once re-set the hub metadata — and refetched the poster — on every
  // render of the player.
  const prevId = prev?.id;
  const nextId = next?.id;
  const onPrev = useCallback(() => {
    if (prevId) goToEpisode(prevId);
  }, [prevId, goToEpisode]);
  const onNext = useCallback(() => {
    if (nextId) goToEpisode(nextId);
  }, [nextId, goToEpisode]);
  useMediaSession({
    item: item.data,
    source,
    videoRef,
    togglePlay: actions.togglePlay,
    seekBy: actions.seekBy,
    seekTo: actions.seekTo,
    prev,
    next,
    onPrev,
    onNext,
  });
  useWakeLock(snap.playing && snap.terminated === null);

  const closeMenu = useCallback(() => {
    setMenu("none");
    setSettingsPanel("root");
  }, []);

  const cycleSubtitles = useCallback(() => {
    const tracks = snap.playback?.subtitles ?? [];
    if (tracks.length === 0) return;
    const cur = snap.subtitle;
    // An "always"-policy burn of a text track occupies that track's cycle
    // slot; an image-format burn isn't in the sidecar list and cycles to Off.
    const curIdx =
      cur.kind === "sidecar"
        ? cur.trackIndex
        : cur.kind === "burn" || cur.kind === "stream"
          ? tracks.findIndex((t) => t.streamIndex === cur.streamIndex)
          : -1;
    if (cur.kind === "off") actions.setSubtitle({ kind: "sidecar", trackIndex: 0 });
    else if (curIdx >= 0 && curIdx + 1 < tracks.length) {
      actions.setSubtitle({ kind: "sidecar", trackIndex: curIdx + 1 });
    } else actions.setSubtitle({ kind: "off" });
  }, [snap.playback, snap.subtitle, actions]);

  const keyHandlers = useMemo(
    () => ({
      togglePlay: actions.togglePlay,
      seekBy: actions.seekBy,
      seekToFraction: (f: number) => {
        const v = videoRef.current;
        if (v?.duration) actions.seekTo(f * v.duration);
      },
      volumeBy: (d: number) => {
        const v = videoRef.current;
        if (v) v.volume = Math.min(1, Math.max(0, v.volume + d));
      },
      toggleMute: () => {
        const v = videoRef.current;
        if (v) v.muted = !v.muted;
      },
      toggleFullscreen: () => toggleFullscreen(videoRef.current),
      cycleSubtitles,
      speedStep: (dir: 1 | -1) => actions.setRate(stepSpeed(snap.rate, dir)),
      nextEpisode: () => next && goToEpisode(next.id),
      prevEpisode: () => prev && goToEpisode(prev.id),
      toggleHelp: () => setHelpOpen((v) => !v),
      escape: () => {
        if (helpOpen) setHelpOpen(false);
        else if (menu !== "none") closeMenu();
        else exit();
      },
      poke: chrome.poke,
    }),
    [actions, cycleSubtitles, snap.rate, next, prev, goToEpisode, helpOpen, menu, closeMenu, exit, chrome.poke],
  );
  usePlayerKeys(keyHandlers);

  const pb = snap.playback;
  const subs = pb?.subtitles ?? [];
  // <track> elements exist for the tracks that still arrive as one file:
  // external sidecars, and every text track when nothing but the original
  // file plays. Embedded tracks of an HLS session are the session's own
  // renditions (hls.js / the native stack add their TextTracks without an
  // element). Only the selected element carries a src: hls.js flips every
  // <track> element to "hidden" and back when it attaches (that is how it
  // clears cues), and Chrome takes the flip as a load — a file with 38
  // embedded tracks once downloaded them all, each a full-file extraction
  // on the server. A src-less element has nothing to fetch; picking a
  // track gives it its src and "hidden" in the same commit.
  const selectedSub = snap.subtitle.kind === "sidecar" ? snap.subtitle.trackIndex : -1;
  const overlayTrack: TrackRef | null =
    snap.subtitle.kind === "sidecar"
      ? { kind: "element", trackIndex: snap.subtitle.trackIndex }
      : snap.subtitle.kind === "stream"
        ? renditionRef(pb, snap.subtitle.streamIndex)
        : null;
  const sheet = !wide;
  const thumbUrl =
    next && next.hasThumb && source.childImageUrl
      ? source.childImageUrl(next.id, "thumb", 576)
      : "";

  const iconBtn = playerIconBtn;
  const chromeCls = (extra: string) =>
    `${extra} transition-[opacity,visibility] duration-200 ${
      chrome.visible ? "visible opacity-100" : "invisible opacity-0"
    }`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black"
      onMouseMove={chrome.poke}
      style={{ cursor: chrome.visible ? "default" : "none" }}
    >
      {/* crossOrigin only when we need CORS for subtitle <track>s — for a
          plain Direct-Play file it adds strictness that breaks cross-origin
          media loading through the relay. */}
      <video
        ref={videoRef}
        className="h-full w-full"
        playsInline
        crossOrigin={subs.length > 0 ? "anonymous" : undefined}
      >
        {subs.map((s, i) =>
          s.external || !pb?.hlsMasterUrl ? (
            <track
              key={`${s.lang}-${s.streamIndex ?? i}`}
              kind="subtitles"
              srcLang={s.lang}
              label={s.label}
              data-track-index={i}
              src={i === selectedSub ? s.url : undefined}
              default={false}
            />
          ) : null,
        )}
      </video>

      <SubtitleOverlay
        videoRef={videoRef}
        track={overlayTrack}
        bindKey={pb?.decisionId}
        color={prefs.subtitleColor}
        size={prefs.subtitleSize}
        position={prefs.subtitlePosition}
        background={prefs.subtitleBackground}
        raise={chrome.visible}
      />

      <TapLayer
        coarse={coarse}
        menuOpen={menu !== "none"}
        chromeVisible={chrome.visible}
        videoRef={videoRef}
        onToggleChrome={() => (chrome.visible ? chrome.hide() : chrome.poke())}
        onTogglePlay={actions.togglePlay}
        onSeekBy={actions.seekBy}
        onCloseMenu={closeMenu}
      />

      <PlayerOverlays
        loading={snap.loading || item.isLoading}
        starting={snap.starting}
        stalled={snap.stalled}
        needsGesture={snap.needsGesture}
        notice={snap.notice}
        terminated={snap.terminated}
        onGesturePlay={actions.togglePlay}
        onRetry={actions.retryAfterTerminate}
        onBack={exit}
      />

      <div
        onMouseEnter={() => setHoverChrome(true)}
        onMouseLeave={() => setHoverChrome(false)}
      >
        <PlayerTopBar
          visible={chrome.visible}
          onBack={exit}
          videoRef={videoRef}
          compact={!wide}
        />
      </div>

      {/* mobile center transport */}
      {!wide ? (
        <div
          className={chromeCls(
            "pointer-events-none absolute inset-0 flex items-center justify-center",
          )}
        >
          <div className="pointer-events-auto">
            <TransportControls
              playing={snap.playing || snap.starting}
              onToggle={actions.togglePlay}
              onSeekBy={actions.seekBy}
              prev={prev}
              next={next}
              onPrev={() => prev && goToEpisode(prev.id)}
              onNext={() => next && goToEpisode(next.id)}
              size="center"
            />
          </div>
        </div>
      ) : null}

      {/* bottom chrome — a gradient scrim, never blurred (the popovers nest
          inside it and the opacity fade would pop a backdrop-filter). Emby's
          stack: title block → seek bar → time row → control row (transport
          centred at ≥sm, utility cluster right). The side inset — 2.1vw, ~30%
          under Emby's 3% (Kenny) — is this band's alone: the title, the bar
          and the transport start on one edge and the icons end on the other;
          the top band hugs the edges at 1rem as before. */}
      <div
        className={chromeCls(
          "rago-player-type absolute right-0 bottom-0 left-0 bg-gradient-to-t from-[rgba(8,8,10,0.85)] via-[rgba(8,8,10,0.45)] to-transparent pl-[max(1rem,2.1vw,env(safe-area-inset-left))] pr-[max(1rem,2.1vw,env(safe-area-inset-right))] pt-20 pb-[max(1.25rem,env(safe-area-inset-bottom))]",
        )}
        onFocusCapture={chrome.poke}
        onMouseEnter={() => setHoverChrome(true)}
        onMouseLeave={() => setHoverChrome(false)}
      >
        {item.data ? (
          <div className="min-w-0">
            <PlayerMetadata item={item.data} />
          </div>
        ) : null}

        {/* seek: a fixed 16px lane absorbs the track's 4→6px hover growth so
            the rows below never shift; SeekBar's own -my-3 hit box overshoots
            the lane by 6px each way, inside the 8px / 4px gaps around it. */}
        <div className="mt-2 flex h-4 flex-col justify-center">
          <SeekBar
            videoRef={videoRef}
            onCommit={actions.seekTo}
            chapters={analysis.chapters}
            previews={analysis.previews}
            previewTileUrl={
              source.previewTileUrl && pb?.partId
                ? (n) => source.previewTileUrl!(pb.partId!, n)
                : undefined
            }
            previewPreloadRadius={coarse ? 0 : 1}
          />
        </div>

        <div className="mt-1 flex items-center justify-between gap-3 text-sm">
          <ElapsedReadout videoRef={videoRef} />
          <RemainingReadout videoRef={videoRef} />
        </div>

        {/* control row: transport centred (≥sm — the phone keeps the centre
            cluster above), utility cluster bottom-right on the same line
            (Kenny). A 1fr·auto·1fr grid keeps the transport on the row's
            true centre whatever the cluster's width; the negative side
            margins put the end glyphs, not their hit boxes, on the band's
            inset edges. */}
        <div className="mt-2 -mx-2 grid grid-cols-[1fr_auto_1fr] items-center">
          <div />
          {wide ? (
            <TransportControls
              playing={snap.playing || snap.starting}
              onToggle={actions.togglePlay}
              onSeekBy={actions.seekBy}
              prev={prev}
              next={next}
              onPrev={() => prev && goToEpisode(prev.id)}
              onNext={() => next && goToEpisode(next.id)}
              size="bar"
            />
          ) : (
            <div />
          )}
          <div className="flex items-center justify-end gap-1">
            {analysis.chapters.length > 0 && pb?.partId ? (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  aria-label="Chapters"
                  aria-haspopup="menu"
                  aria-expanded={menu === "chapters"}
                  className={iconBtn}
                  onClick={() => setMenu(menu === "chapters" ? "none" : "chapters")}
                >
                  <ChaptersIcon size={22} />
                </button>
                <ChaptersMenu
                  open={menu === "chapters"}
                  onClose={closeMenu}
                  sheet={sheet}
                  chapters={analysis.chapters}
                  thumbUrl={(i) => source.chapterThumbUrl?.(pb.partId!, i, 320) ?? ""}
                  videoRef={videoRef}
                  onJump={actions.seekTo}
                />
              </div>
            ) : null}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Subtitles"
                aria-haspopup="menu"
                aria-expanded={menu === "subs"}
                className={iconBtn}
                onClick={() => setMenu(menu === "subs" ? "none" : "subs")}
              >
                <CaptionsIcon size={22} />
              </button>
              {item.data ? (
                <SubtitlesMenu
                  open={menu === "subs"}
                  onClose={closeMenu}
                  sheet={sheet}
                  item={item.data}
                  playback={pb}
                  burnPolicy={prefs.burnSubtitles}
                  snap={snap}
                  actions={actions}
                  onSearch={isServer ? () => setSubSearchOpen(true) : undefined}
                />
              ) : null}
            </div>
            {item.data && audioStreams(item.data).length > 1 ? (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  aria-label="Audio track"
                  aria-haspopup="menu"
                  aria-expanded={menu === "audio"}
                  className={iconBtn}
                  onClick={() => setMenu(menu === "audio" ? "none" : "audio")}
                >
                  <AudioTrackIcon size={22} />
                </button>
                <AudioMenu
                  open={menu === "audio"}
                  onClose={closeMenu}
                  sheet={sheet}
                  item={item.data}
                  snap={snap}
                  actions={actions}
                />
              </div>
            ) : null}
            {/* Speed is the settings menu's speed panel: a second click on
                the gauge closes it (Kenny), a click while the menu is on
                another panel just switches to speed. */}
            <button
              type="button"
              aria-label="Playback speed"
              aria-haspopup="menu"
              aria-expanded={menu === "settings" && settingsPanel === "speed"}
              className={iconBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (menu === "settings" && settingsPanel === "speed") closeMenu();
                else {
                  setSettingsPanel("speed");
                  setMenu("settings");
                }
              }}
            >
              <GaugeIcon size={22} />
            </button>
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Playback settings"
                aria-haspopup="menu"
                aria-expanded={menu === "settings"}
                className={iconBtn}
                onClick={() => {
                  if (menu === "settings") closeMenu();
                  else {
                    setSettingsPanel("root");
                    setMenu("settings");
                  }
                }}
              >
                <GearIcon size={22} />
              </button>
              {item.data ? (
                <SettingsMenu
                  open={menu === "settings"}
                  onClose={closeMenu}
                  sheet={sheet}
                  panel={settingsPanel}
                  onPanel={setSettingsPanel}
                  item={item.data}
                  isServer={isServer}
                  snap={snap}
                  actions={actions}
                  hasEpisodes={next !== null || prev !== null}
                  showShortcuts={!coarse}
                  onOpenShortcuts={() => setHelpOpen(true)}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {upNext.visible && next ? (
        <UpNextCard
          next={next}
          thumbUrl={thumbUrl}
          countdown={upNext.countdown}
          onPlayNow={upNext.playNow}
          onDismiss={upNext.dismiss}
        />
      ) : null}

      {analysis.markers.length > 0 ? (
        <SkipMarkerPill
          videoRef={videoRef}
          markers={analysis.markers}
          hasNext={next !== null}
          upNextVisible={upNext.visible}
          chromeVisible={chrome.visible}
          onSeek={actions.seekTo}
        />
      ) : null}

      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      {isServer && item.data ? (
        <SubtitleSearchDialog
          open={subSearchOpen}
          onClose={() => setSubSearchOpen(false)}
          source={source}
          item={item.data}
          partId={pb?.partId}
          onDownloaded={(index) => actions.refreshTracks(index)}
        />
      ) : null}
    </div>
  );
}
